import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "../common/Logo";
import Cta from "../common/Cta";
import { primaryNav } from "../../lib/content";

const clamp01 = (v) => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [heroOpacity, setHeroOpacity] = useState(0);
  const [overHero, setOverHero] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const heroSelector = isHome
    ? '[data-testid="home-video-hero"]'
    : pathname === "/mythoros"
    ? '[data-testid="agent-video-hero"]'
    : null;

  useEffect(() => {
    const compute = () => {
      setScrolled(window.scrollY > 8);
      if (heroSelector) {
        const hero = document.querySelector(heroSelector);
        // Header is over the dark hero while that section still covers the top of the viewport.
        setOverHero(hero ? hero.getBoundingClientRect().bottom > 88 : false);
      } else {
        setOverHero(false);
      }
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [heroSelector]);

  // On the homepage the header stays hidden through the cinematic intro, then slowly appears as you scroll into the hero.
  useEffect(() => {
    setHeroOpacity(isHome ? 0 : 1);
    if (isHome) {
      const onNav = (e) => setHeroOpacity(clamp01(e.detail?.opacity));
      window.addEventListener("kv:hero-nav", onNav);
      return () => window.removeEventListener("kv:hero-nav", onNav);
    }
  }, [isHome]);

  const navOpacity = open ? 1 : isHome ? heroOpacity : 1;
  const hidden = navOpacity <= 0.01 && !open;

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Transparent + white content the whole time it's over the hero video (blends into it);
  // becomes the solid light header once scrolled past the hero into the site below.
  const darkMode = overHero && !open;

  return (
    <header
      style={{ opacity: navOpacity, transform: `translateY(${(1 - navOpacity) * -12}px)`, pointerEvents: hidden ? "none" : "auto" }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        darkMode
          ? "bg-transparent border-b border-transparent"
          : scrolled
          ? "bg-kv-bg border-b border-kv-steel/20 shadow-[0_1px_0_rgba(16,19,26,0.05)]"
          : "bg-kv-bg border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-[76px] md:h-[84px] flex items-center justify-between gap-6">
        <Logo variant="horizontal" tone={darkMode ? "dark" : "light"} className="h-8 md:h-10 w-auto" />

        <div className="hidden lg:flex items-center gap-8">
          {primaryNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`kv-link font-body text-base ${
                darkMode
                  ? pathname === item.to ? "text-white" : "text-white/70 hover:text-white"
                  : pathname === item.to ? "text-kv-off" : "text-kv-grey hover:text-kv-off"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            {darkMode ? (
              <Link to="/contact" data-testid="nav-strategy-call" className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium bg-white text-black border border-white hover:bg-[#e6e8ec] transition-colors">
                Book a Strategy Call
              </Link>
            ) : (
              <Cta to="/contact" testId="nav-strategy-call" className="px-6 py-3 text-sm">
                Book a Strategy Call
              </Cta>
            )}
          </div>
          <button
            className={`lg:hidden inline-flex items-center justify-center h-10 w-10 border ${darkMode ? "border-white/30 text-white" : "border-kv-steel/40 text-kv-off"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-kv-bg border-t border-kv-steel/20"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {primaryNav.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`font-display text-xl py-3 border-b border-kv-steel/10 ${
                    pathname === item.to ? "text-kv-electric" : "text-kv-off"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Cta to="/contact" testId="mobile-strategy-call" className="mt-5 w-full" arrow>
                Book a Strategy Call
              </Cta>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
